import { Box, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import CusDataGrid from '../../../components/material-ui/CusDataGrid';
import { useTranslation } from 'react-i18next';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import TypographyText from '../../../components/material-ui/table/TypographyText';

export default function PlayersPivot({ payouts }) {
  const { t } = useTranslation();

  const infoTable = useMemo(() => {
    const info = {};
    payouts.forEach((el) => {
      const curCode = el[5];
      if (!info[curCode]) info[curCode] = { code: curCode };
      if (!info[curCode].count) info[curCode].count = 0;
      if (!info[curCode].sum) info[curCode].sum = 0;
      if (!info[curCode].ourCommission) info[curCode].ourCommission = 0;
      if (!info[curCode].gateCommission) info[curCode].gateCommission = 0;
      if (!info[curCode].receivingSum) info[curCode].receivingSum = 0;

      info[curCode].count++;
      info[curCode].sum += el[2];
      info[curCode].ourCommission += el[3];
      info[curCode].gateCommission += el[19];
      info[curCode].receivingSum += el[4];
    });

    return info;
  }, [payouts.length]);

  const rows = Object.values(infoTable).map((el) => {
    const casinoCosts = el.ourCommission - el.gateCommission;
    const casinoCostsText = casinoCosts > 0 ? '+' + casinoCosts.toFixed(2) : casinoCosts.toFixed(2);

    return {
      id: el.code,
      currency: el.code,
      count: el.count,
      sum: el.sum.toFixed(2),
      ourCommission: el.ourCommission.toFixed(2),
      gateCommission: el.gateCommission.toFixed(2),
      ourDiffGateCommission: casinoCostsText,
      receivingSum: el.receivingSum.toFixed(2),
      withdrawalGate: (el.gateCommission + el.receivingSum).toFixed(2)
    };
  });

  const column = [
    {
      field: 'currency',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('Currency')}</TypographyText>{' '}
          <CusTooltip text={'Currency of payment requests.'} />
        </Box>
      ),
      width: 85,
      minWidth: 85
    },
    {
      field: 'count',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('Orders')}</TypographyText>{' '}
          <CusTooltip text={'Number of requests for payment to players.'} />
        </Box>
      ),
      width: 85,
      align: 'right',
      minWidth: 85
    },
    {
      field: 'sum',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('Amount of orders')}</TypographyText>{' '}
          <CusTooltip text={'The amount withdrawn from the players main balance.'} />
        </Box>
      ),
      flex: 1,
      align: 'right',
      width: 160
    },
    {
      field: 'ourCommission',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('Casino fee')}</TypographyText>{' '}
          <CusTooltip
            text={'The casino commission for paying out to the player. Paid by the player.'}
          />
        </Box>
      ),
      flex: 1,
      align: 'right',
      width: 150
    },

    {
      field: 'receivingSum',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('Amount to receive')}</TypographyText>{' '}
          <CusTooltip text={'The amount of payment received by the player.'} />
        </Box>
      ),
      // flex: 1,
      align: 'right',
      width: 165
    },
    {
      field: 'gateCommission',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('PSP fee')}</TypographyText>{' '}
          <CusTooltip
            text={
              "The payment provider's commission for paying out to the player from the amount received by the player."
            }
          />
        </Box>
      ),
      flex: 1,
      align: 'right',
      width: 150
    },
    {
      field: 'ourDiffGateCommission',
      align: 'right',
      headerName: (
        <Box lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('Casino expenses')}</TypographyText>{' '}
          <CusTooltip
            text={
              <Stack>
                <Typography>
                  {t(
                    'The casinos actual costs for processing a payout to a player via a Payment Service Provider (PSP).'
                  )}
                </Typography>
                <Typography>{t("'Casino expenses' = 'Casino fee' - 'PSP fee'.")}</Typography>
                <Typography fontStyle={'italic'}>
                  {t(
                    'A positive amount means that the casino made money by paying out to players.'
                  )}
                </Typography>
              </Stack>
            }
          />
        </Box>
      ),
      flex: 1,
      width: 140
    },
    {
      field: 'withdrawalGate',
      headerName: (
        <Box gap={1} lineHeight={'100%'} textAlign={'end'}>
          <TypographyText>{t('PSP payments')}</TypographyText>{' '}
          <CusTooltip text={'The actual withdrawal amount from Payment Service Provider (PSP).'} />
        </Box>
      ),
      flex: 1,
      align: 'right',
      width: 150
    }
  ];

  return (
    <Stack gap={1} width={'100%'}>
      <CusDataGrid
        sx={{
          '& .MuiDataGrid-columnHeader': {
            padding: 0.75
          }
        }}
        labelFontWeight={600}
        label={'Total to be paid'}
        rows={rows}
        columns={column.map((el) => ({ ...el, headerClassName: 'bold-header' }))}
        checkboxSelection={false}
      />
    </Stack>
  );
}
